import React, { useState, useEffect, useCallback } from 'react';
import { getCandidates, createCandidate, updateCandidate, deleteCandidate } from '../../api';
import '../../styles/AdminStyles.css';

const CandidatesAdmin = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', position: '', bio: '', pageContent: '' });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [achievementFiles, setAchievementFiles] = useState([]);
  const [achievementPreviews, setAchievementPreviews] = useState([]);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCandidates();
      setCandidates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Ошибка загрузки кандидатов:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      achievementPreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [photoPreview, achievementPreviews]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleAchievementFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    const updatedFiles = [...achievementFiles, ...selectedFiles];
    setAchievementFiles(updatedFiles);
    const newPreviews = updatedFiles.map(file => URL.createObjectURL(file));
    achievementPreviews.forEach(url => URL.revokeObjectURL(url));
    setAchievementPreviews(newPreviews);
    e.target.value = '';
  };

  const removeAchievementFile = (indexToRemove) => {
    const updatedFiles = achievementFiles.filter((_, idx) => idx !== indexToRemove);
    setAchievementFiles(updatedFiles);
    const newPreviews = updatedFiles.map(file => URL.createObjectURL(file));
    achievementPreviews.forEach(url => URL.revokeObjectURL(url));
    setAchievementPreviews(newPreviews);
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ name: '', position: '', bio: '', pageContent: '' });
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
    setAchievementFiles([]);
    achievementPreviews.forEach(url => URL.revokeObjectURL(url));
    setAchievementPreviews([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('position', form.position);
    formData.append('bio', form.bio);
    formData.append('pageContent', form.pageContent);
    if (photoFile) formData.append('photo', photoFile);
    achievementFiles.forEach(file => formData.append('achievement_images', file));

    try {
      if (editing) {
        await updateCandidate(editing.id, formData);
      } else {
        await createCandidate(formData);
      }
      resetForm();
      fetchCandidates();
    } catch (err) {
      console.error(err);
      alert('Ошибка сохранения');
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      position: item.position || '',
      bio: item.bio || '',
      pageContent: item.pageContent || '',
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setAchievementFiles([]);
    setAchievementPreviews([]);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить кандидата? Все изображения также будут удалены.')) {
      try {
        await deleteCandidate(id);
        fetchCandidates();
      } catch (err) {
        console.error(err);
        alert('Ошибка удаления');
      }
    }
  };

  return (
    <div className="admin-wrapper">
      <div className="admin-header">
        <h1 className="admin-title">Управление кандидатами/депутатами</h1>
        <button onClick={() => window.location.href = '/admin'} className="admin-logout-btn">Назад</button>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">{editing ? 'Редактировать кандидата' : 'Добавить кандидата'}</div>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-form-row">
            <label>ФИО *</label>
            <input type="text" name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="admin-form-row">
            <label>Должность/статус</label>
            <input type="text" name="position" value={form.position} onChange={handleChange} />
          </div>
          <div className="admin-form-row">
            <label>Краткая биография</label>
            <textarea name="bio" value={form.bio} onChange={handleChange} rows="3" />
          </div>
          <div className="admin-form-row">
            <label>Полная страница (HTML)</label>
            <textarea name="pageContent" value={form.pageContent} onChange={handleChange} rows="6" />
          </div>
          <div className="admin-form-row">
            <label>Фото</label>
            <input type="file" onChange={handlePhotoChange} accept="image/*" />
            {photoPreview && (
              <div className="image-preview-grid">
                <div className="image-preview"><img src={photoPreview} alt="preview" /></div>
              </div>
            )}
            {editing && editing.photo && !photoFile && (
              <div>Текущее фото: <img src={`http://localhost:5000${editing.photo}`} alt="current" style={{ width: '80px', height: '80px', objectFit: 'cover' }} /></div>
            )}
          </div>
          <div className="admin-form-row">
            <label>Изображения достижений (можно несколько)</label>
            <input type="file" multiple onChange={handleAchievementFilesChange} accept="image/*" />
            {achievementPreviews.length > 0 && (
              <div className="image-preview-grid">
                {achievementPreviews.map((url, idx) => (
                  <div key={idx} className="image-preview" style={{ position: 'relative' }}>
                    <img src={url} alt="preview" />
                    <button
                      type="button"
                      className="image-preview-remove"
                      onClick={() => removeAchievementFile(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="admin-btn-group">
            <button type="submit" className="admin-btn admin-btn-primary">{editing ? 'Обновить' : 'Создать'}</button>
            {editing && <button type="button" onClick={resetForm} className="admin-btn admin-btn-secondary">Отмена</button>}
          </div>
        </form>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">Список кандидатов</div>
        {loading && <div className="loading-spinner">Загрузка...</div>}
        {!loading && candidates.length === 0 && <div>Нет кандидатов</div>}
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr><th>ID</th><th>ФИО</th><th>Должность</th><th>Фото</th><th>Действия</th></tr>
            </thead>
            <tbody>
              {candidates.map(item => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.name}</td>
                  <td>{item.position}</td>
                  <td>
                    {item.photo && <img src={`http://localhost:5000${item.photo}`} alt="thumb" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />}
                  </td>
                  <td>
                    <div className="admin-btn-group">
                      <button onClick={() => handleEdit(item)} className="admin-btn admin-btn-secondary">Ред.</button>
                      <button onClick={() => handleDelete(item.id)} className="admin-btn admin-btn-danger">Уд.</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CandidatesAdmin;